import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react'
import { supabase, API_URL } from '../lib/supabase'
import { notify } from '../lib/notify'
import type { User, Session } from '@supabase/supabase-js'
import { platform } from '../services/platform'
import { api } from '../services/api'
import { requestJson, withTimeout, HttpError, BOOT_TIMEOUT_MS, DEFAULT_TIMEOUT_MS } from '../lib/http'
import { config } from '../config'
import {
    pickPrimaryRole,
    roleAfterProfileFailure,
    UNRESOLVED_ROLE,
    type UserRole
} from '../lib/roles'

export type { UserRole }
export { pickPrimaryRole }

export type AccessLevel = 'full' | 'view' | 'limited' | 'none'

export type SubscriptionStatus = 'none' | 'trial' | 'active' | 'past_due' | 'grace_period' | 'cancelled' | 'expired'

export interface SubscriptionInfo {
    status: SubscriptionStatus
    planId?: string
    planName?: string
    isActive: boolean
    inGracePeriod: boolean
    trialEndsAt?: string
    gracePeriodEndsAt?: string
    currentPeriodEnd?: string
}

export interface RolePermissions {
    overview: AccessLevel
    tenderSetup: AccessLevel
    dailyWorkLog: AccessLevel
    buyerEvaluation: AccessLevel
    inspectionDesk: AccessLevel
    locksAudit: AccessLevel
    reporting: AccessLevel
    settings: AccessLevel
    userManagement: AccessLevel
    stationManagement: AccessLevel
    subscription: AccessLevel
    paymentManagement: AccessLevel
    qrCodes: AccessLevel
    inspections: AccessLevel
    contractorStaff: AccessLevel
    scanUpload: AccessLevel
    supplies: AccessLevel
    chiAttendance: AccessLevel
    staffDeployment: AccessLevel
    // LOA Execution Spine (added 2026-05-12)
    cycleScheduler: AccessLevel
    penaltyLedger: AccessLevel
    weeklyScores: AccessLevel
    invoices: AccessLevel
    complaints: AccessLevel
    complianceVault: AccessLevel
    contractDocs: AccessLevel
    complaintPosters: AccessLevel
}

export const rolePermissions: Record<UserRole, RolePermissions> = {
    super_admin: {
        overview: 'full',
        tenderSetup: 'full',
        dailyWorkLog: 'full',
        buyerEvaluation: 'full',
        inspectionDesk: 'full',
        locksAudit: 'full',
        reporting: 'full',
        settings: 'full',
        userManagement: 'full',
        stationManagement: 'full',
        subscription: 'full',
        paymentManagement: 'full',
        qrCodes: 'full',
        inspections: 'full',
        contractorStaff: 'full',
        scanUpload: 'full',
        supplies: 'full',
        chiAttendance: 'full',
        staffDeployment: 'full',
        cycleScheduler: 'full',
        penaltyLedger: 'full',
        weeklyScores: 'full',
        invoices: 'full',
        complaints: 'full',
        complianceVault: 'full',
        contractDocs: 'full',
        complaintPosters: 'full',
    },
    admin: {
        overview: 'full',
        tenderSetup: 'full',
        dailyWorkLog: 'full',
        buyerEvaluation: 'full',
        inspectionDesk: 'full',
        locksAudit: 'full',
        reporting: 'full',
        settings: 'full',
        userManagement: 'full',
        stationManagement: 'full',
        subscription: 'none',
        paymentManagement: 'none',
        qrCodes: 'full',
        inspections: 'full',
        contractorStaff: 'full',
        scanUpload: 'full',
        supplies: 'full',
        chiAttendance: 'full',
        staffDeployment: 'full',
        cycleScheduler: 'full',
        penaltyLedger: 'full',
        weeklyScores: 'full',
        invoices: 'full',
        complaints: 'full',
        complianceVault: 'full',
        contractDocs: 'full',
        complaintPosters: 'full',
    },
    contractor: {
        // Sidebar limited to: daily ops, their workforce, billing.
        // Read-only audit on penalties + scores so they can see what hit them.
        overview: 'limited',
        dailyWorkLog: 'full',
        contractorStaff: 'full',
        staffDeployment: 'full',
        supplies: 'view',
        reporting: 'limited',
        cycleScheduler: 'view',
        complaints: 'full',
        complianceVault: 'full',
        contractDocs: 'full',
        complaintPosters: 'view',
        penaltyLedger: 'view',
        weeklyScores: 'view',
        invoices: 'full',
        subscription: 'full',
        settings: 'limited',
        // Hidden — not relevant for contractor day-to-day.
        tenderSetup: 'none',
        buyerEvaluation: 'none',
        inspectionDesk: 'none',
        inspections: 'none',
        locksAudit: 'none',
        userManagement: 'none',
        stationManagement: 'none',
        paymentManagement: 'none',
        qrCodes: 'none',
        scanUpload: 'none',
        chiAttendance: 'none',
    },
    buyer: {
        // Buyers do day-to-day approvals from /buyer/queue (penalty / docs /
        // invoices / weekly scores). The admin sidebar tabs are kept as
        // 'view' for audit only — actions stay in the dedicated portal.
        overview: 'limited',
        buyerEvaluation: 'full',
        chiAttendance: 'full',
        staffDeployment: 'view',
        qrCodes: 'full',
        complaintPosters: 'full',
        tenderSetup: 'view',
        cycleScheduler: 'view',
        complaints: 'view',
        contractorStaff: 'view',
        contractDocs: 'view',
        penaltyLedger: 'view',
        weeklyScores: 'view',
        invoices: 'view',
        complianceVault: 'view',
        settings: 'limited',
        // Hidden — not in buyer scope.
        dailyWorkLog: 'none',
        inspectionDesk: 'none',
        inspections: 'none',
        locksAudit: 'none',
        reporting: 'none',
        supplies: 'none',
        userManagement: 'none',
        stationManagement: 'none',
        subscription: 'none',
        paymentManagement: 'none',
        scanUpload: 'none',
    },
    inspector: {
        // Inspectors live in /inspector/* only. Admin console tabs stay hidden
        // so a delayed bounce cannot surface Penalty Ledger / audit views.
        overview: 'none',
        inspectionDesk: 'none',
        inspections: 'none',
        cycleScheduler: 'none',
        penaltyLedger: 'none',
        complaints: 'none',
        settings: 'limited',
        tenderSetup: 'none',
        dailyWorkLog: 'none',
        buyerEvaluation: 'none',
        locksAudit: 'none',
        reporting: 'none',
        userManagement: 'none',
        stationManagement: 'none',
        subscription: 'none',
        paymentManagement: 'none',
        qrCodes: 'none',
        contractorStaff: 'none',
        scanUpload: 'none',
        supplies: 'none',
        chiAttendance: 'none',
        staffDeployment: 'none',
        weeklyScores: 'none',
        invoices: 'none',
        complianceVault: 'none',
        contractDocs: 'none',
        complaintPosters: 'none',
    },
    contractor_staff: {
        overview: 'none',
        tenderSetup: 'none',
        dailyWorkLog: 'none',
        buyerEvaluation: 'none',
        inspectionDesk: 'none',
        locksAudit: 'none',
        reporting: 'none',
        settings: 'limited',
        userManagement: 'none',
        stationManagement: 'none',
        subscription: 'none',
        paymentManagement: 'none',
        qrCodes: 'none',
        inspections: 'none',
        contractorStaff: 'none',
        scanUpload: 'none',
        supplies: 'none',
        chiAttendance: 'none',
        staffDeployment: 'none',
        // Staff use the dedicated PWA, not the admin tabs.
        cycleScheduler: 'none',
        penaltyLedger: 'none',
        weeklyScores: 'none',
        invoices: 'none',
        complaints: 'none',
        complianceVault: 'none',
        contractDocs: 'none',
        complaintPosters: 'none',
    },
}

// Map nav item names to permission keys
export const navToPermissionKey: Record<string, keyof RolePermissions> = {
    'Overview': 'overview',
    'Tender Setup': 'tenderSetup',
    'Work Log': 'dailyWorkLog',
    'Buyer Evaluation': 'buyerEvaluation',
    'Submission Review': 'inspectionDesk',
    'Locks & Audit': 'locksAudit',
    'Reporting': 'reporting',
    'Compliance Reports': 'reporting',
    'Settings': 'settings',
    'User Management': 'userManagement',
    'Station Management': 'stationManagement',
    'Subscription': 'subscription',
    'Payment Management': 'paymentManagement',
    'QR Codes': 'qrCodes',
    'Inspections': 'inspections',
    'Contractor Staff': 'contractorStaff',
    'Scan & Upload': 'scanUpload',
    'Supplies': 'supplies',
    'CHI Attendance': 'chiAttendance',
    'Staff Deployment': 'staffDeployment',
    // LOA Execution Spine tabs
    'Cycle Scheduler': 'cycleScheduler',
    'Penalty Ledger': 'penaltyLedger',
    'Weekly Scores': 'weeklyScores',
    'Invoices': 'invoices',
    'Complaints': 'complaints',
    'Compliance Vault': 'complianceVault',
    'Contract Documents': 'contractDocs',
    'Complaint Posters': 'complaintPosters',
}

export interface UserProfile {
    id: string
    email: string
    full_name: string
    phone?: string
    avatar_url?: string
    organization?: string
    station?: string
    assigned_stations?: string[] | null
    roles: UserRole[]
    is_super_admin?: boolean
    subscription_status?: string
}

export type SetRoleOptions = {
    displayName?: string
    /** Full profile snapshot for Login As (evaluator/inspector/admin/contractor). */
    profile?: Partial<UserProfile> & { id: string }
}

interface BiometricStatus {
    isAvailable: boolean
    isEnabled: boolean
    biometryType: string | null
}

interface AuthContextType {
    // User state
    user: User | null
    profile: UserProfile | null
    session: Session | null
    loading: boolean

    // Role-based access (backward compatible)
    role: UserRole
    setRole: (role: UserRole, options?: SetRoleOptions) => void
    permissions: RolePermissions
    hasAccess: (page: keyof RolePermissions) => boolean
    canEdit: (page: keyof RolePermissions) => boolean

    // Login As (admin impersonation)
    isImpersonating: boolean
    impersonatedDisplayName: string | null
    impersonatedUserId: string | null
    originalRole: UserRole | null
    exitImpersonation: () => void

    /** True when the profile could not be loaded, so role/permissions are stale. */
    profileDegraded: boolean
    /** Re-attempts the profile load. Resolves true once the role is authoritative. */
    retryProfile: () => Promise<boolean>

    // Super Admin & Subscription
    isSuperAdmin: boolean
    subscription: SubscriptionInfo
    refreshSubscription: () => Promise<void>

    // Auth actions
    login: (email: string, password: string) => Promise<{ error?: string }>
    loginWithOtp: (tokenHash: string) => Promise<{ error?: string }>
    signup: (email: string, password: string, fullName: string) => Promise<{ error?: string }>
    loginWithGoogle: () => Promise<void>
    logout: () => Promise<void>

    // Biometric auth (native only)
    biometric: BiometricStatus
    enableBiometric: (email: string, password: string) => Promise<boolean>
    disableBiometric: () => Promise<boolean>
    loginWithBiometric: () => Promise<{ error?: string }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const SUPER_ADMIN_EMAIL = config.superAdminEmail ?? ''

type BiometricAuthMod = typeof import('../services/biometricAuth').biometricAuth
let biometricAuthCache: BiometricAuthMod | null = null
async function loadBiometricAuth (): Promise<BiometricAuthMod> {
  if (!biometricAuthCache) {
    const m = await import('../services/biometricAuth')
    biometricAuthCache = m.biometricAuth
  }
  return biometricAuthCache
}

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null)
    const [session, setSession] = useState<Session | null>(null)
    const [loading, setLoading] = useState(true)
    const [role, setRole] = useState<UserRole>(UNRESOLVED_ROLE)
    const [originalRole, setOriginalRole] = useState<UserRole | null>(null)
    const [isImpersonating, setIsImpersonating] = useState(false)
    const [impersonatedDisplayName, setImpersonatedDisplayName] = useState<string | null>(null)
    const [impersonatedProfile, setImpersonatedProfile] = useState<UserProfile | null>(null)
    const [authProfile, setAuthProfile] = useState<UserProfile | null>(null)
    const [isSuperAdmin, setIsSuperAdmin] = useState(false)
    // True when the session is authenticated but the profile could not be
    // loaded, so role/permissions are the last known values rather than fresh.
    const [profileDegraded, setProfileDegraded] = useState(false)
    const [subscription, setSubscription] = useState<SubscriptionInfo>({
        status: 'none',
        isActive: false,
        inGracePeriod: false
    })
    
    // Biometric state
    const [biometric, setBiometric] = useState<BiometricStatus>({
        isAvailable: false,
        isEnabled: false,
        biometryType: null
    })

    // Tracks a session we already know about so SIGNED_IN from tab refocus/restore does not
    // re-trigger the full-app loading overlay (ProtectedRoute train).
    const lastAuthedUserIdRef = useRef<string | null>(null)
    const isImpersonatingRef = useRef(false)

    // Effective profile for UI: Login As target when impersonating, else real auth profile.
    const profile =
        isImpersonating && impersonatedProfile ? impersonatedProfile : authProfile

    /** Drop React auth state immediately so Sign Out cannot wait on a hung SDK call. */
    const clearLocalSession = () => {
        api.clearAuthCache()
        api.setImpersonationUserId(null)
        lastAuthedUserIdRef.current = null
        isImpersonatingRef.current = false
        setSession(null)
        setUser(null)
        setAuthProfile(null)
        setImpersonatedProfile(null)
        setRole(UNRESOLVED_ROLE)
        setIsImpersonating(false)
        setOriginalRole(null)
        setImpersonatedDisplayName(null)
        setProfileDegraded(false)
        setLoading(false)
    }

    /** Best-effort SDK sign-out. Must never block the UI; GoTrue can deadlock or hang. */
    const persistSignOut = async () => {
        try {
            await withTimeout(supabase.auth.signOut(), BOOT_TIMEOUT_MS)
        } catch (err) {
            console.warn('[Auth] signOut failed or timed out; trying local scope:', err)
            try {
                await withTimeout(supabase.auth.signOut({ scope: 'local' }), 2_000)
            } catch (localErr) {
                console.warn('[Auth] local signOut failed:', localErr)
            }
        }
    }

    // Check biometric availability on mount (Capacitor only — lazy-load native module)
    useEffect(() => {
        const checkBiometric = async () => {
            if (!platform.isNative()) return
            const biometricAuth = await loadBiometricAuth()
            const { available, biometryType } = await biometricAuth.isAvailable()
            const hasCredentials = await biometricAuth.hasStoredCredentials()
            setBiometric({
                isAvailable: available,
                isEnabled: hasCredentials,
                biometryType: biometryType ? biometricAuth.getBiometryTypeName(biometryType) : null
            })
        }
        void checkBiometric()
    }, [])

    /** Session read with a hard timeout — the SDK exposes no abort signal. */
    const currentAccessToken = async (): Promise<string | undefined> => {
        try {
            const { data } = await withTimeout(supabase.auth.getSession(), BOOT_TIMEOUT_MS)
            return data.session?.access_token
        } catch (err) {
            console.warn('[Auth] could not read session:', err)
            return undefined
        }
    }

    // Fetch subscription status
    const fetchSubscription = async (accessToken?: string) => {
        try {
            const token = accessToken || (await currentAccessToken())
            if (!token) return

            interface SubscriptionStatusResponse {
                success?: boolean
                data?: {
                    status?: SubscriptionStatus
                    planId?: string
                    isActive?: boolean
                    inGracePeriod?: boolean
                    trialEndsAt?: string
                    gracePeriodEndsAt?: string
                }
            }

            const body = await requestJson<SubscriptionStatusResponse>(
                `${API_URL}/api/subscriptions/status`,
                { headers: { 'Authorization': `Bearer ${token}` }, timeoutMs: DEFAULT_TIMEOUT_MS }
            )

            if (body.success && body.data) {
                setSubscription({
                    status: body.data.status || 'none',
                    planId: body.data.planId,
                    isActive: body.data.isActive || false,
                    inGracePeriod: body.data.inGracePeriod || false,
                    trialEndsAt: body.data.trialEndsAt,
                    gracePeriodEndsAt: body.data.gracePeriodEndsAt
                })
            }
        } catch (error) {
            console.error('Error fetching subscription:', error)
        }
    }

    // Refresh subscription (callable from components)
    const refreshSubscription = async () => {
        await fetchSubscription(await currentAccessToken())
    }

    function normalizeProfileRoles (raw: unknown): UserRole[] {
        if (!Array.isArray(raw)) return []
        return raw.filter((r): r is UserRole => typeof r === 'string')
    }

    async function loadProfileFromApi (userId: string, token: string | undefined): Promise<'ok' | 'inactive' | 'fail'> {
        let payload: { code?: string; success?: boolean; data?: unknown }
        try {
            payload = await requestJson(`${API_URL}/api/users/${userId}`, {
                headers: { 'Authorization': `Bearer ${token || ''}` },
                timeoutMs: BOOT_TIMEOUT_MS
            })
        } catch (err) {
            if (err instanceof HttpError && err.code === 'ACCOUNT_INACTIVE') {
                notify.error('Your account has been deactivated. Contact an administrator.')
                clearLocalSession()
                void persistSignOut()
                return 'inactive'
            }
            // HTTP error, timeout, or transport failure — all indistinguishable
            // from "offline" here, and all must leave the current role untouched.
            console.warn('[Auth] profile request failed:', err)
            return 'fail'
        }
        if (!payload.success || !payload.data) return 'fail'
        if (lastAuthedUserIdRef.current !== userId) return 'fail'

        const roles = normalizeProfileRoles((payload.data as { roles?: unknown }).roles)
        const dataPayload = { ...payload.data, roles } as Record<string, unknown>
        setAuthProfile(dataPayload as never)

        const superAdmin =
            dataPayload.is_super_admin === true ||
            (SUPER_ADMIN_EMAIL.length > 0 && dataPayload.email === SUPER_ADMIN_EMAIL)
        setIsSuperAdmin(superAdmin)

        // Do not clobber an active Login As session on profile refresh.
        if (isImpersonatingRef.current) {
            return 'ok'
        }

        if (superAdmin) {
            setRole('super_admin')
            setIsImpersonating(false)
            setOriginalRole(null)
            setImpersonatedProfile(null)
            setImpersonatedDisplayName(null)
            api.setImpersonationUserId(null)
        } else {
            const primaryRole = pickPrimaryRole(roles)
            if (!primaryRole) {
                // Authenticated but with no roles assigned: leave the user on the
                // permissionless default rather than inferring one.
                console.warn('[Auth] profile loaded with no roles assigned')
                setRole(UNRESOLVED_ROLE)
                return 'ok'
            }
            setRole(primaryRole)
            if (primaryRole === 'admin') {
                setIsImpersonating(false)
                setOriginalRole(null)
                setImpersonatedProfile(null)
                setImpersonatedDisplayName(null)
                api.setImpersonationUserId(null)
            }
        }

        if (roles.includes('contractor') || superAdmin) {
            if (lastAuthedUserIdRef.current !== userId) return 'fail'
            await fetchSubscription(token)
        }
        return 'ok'
    }

    /**
     * Loads the profile and resolves the role. Retries once, then **fails
     * closed**.
     *
     * This previously fell back to a synthetic profile with `roles: []`, which
     * resolved to `buyer` — so a staff member on a weak connection silently got
     * the buyer console, every action 403'd, and the role flip crashed the admin
     * shell. A failure now leaves the established role alone and reports back so
     * the caller can surface a retry.
     */
    const fetchProfile = async (userId: string, accessToken?: string): Promise<boolean> => {
        try {
            if (lastAuthedUserIdRef.current !== userId) return false
            const token = accessToken || (await currentAccessToken())
            if (lastAuthedUserIdRef.current !== userId) return false
            let outcome = await loadProfileFromApi(userId, token)
            if (lastAuthedUserIdRef.current !== userId) return false
            if (outcome === 'ok') { setProfileDegraded(false); return true }
            if (outcome === 'inactive') return false

            await new Promise(r => setTimeout(r, 450))
            if (lastAuthedUserIdRef.current !== userId) return false
            outcome = await loadProfileFromApi(userId, await currentAccessToken())
            if (lastAuthedUserIdRef.current !== userId) return false
            if (outcome === 'ok') { setProfileDegraded(false); return true }
            if (outcome === 'inactive') return false

            // Both attempts failed. Super admin is identified by email on the
            // session alone, so that one case can still be resolved offline.
            const { data: { session: remaining } } = await withTimeout(
                supabase.auth.getSession(),
                BOOT_TIMEOUT_MS
            )
            if (lastAuthedUserIdRef.current !== userId) return false
            const currentUser = remaining?.user
            if (currentUser && SUPER_ADMIN_EMAIL.length > 0 && currentUser.email === SUPER_ADMIN_EMAIL) {
                setIsSuperAdmin(true)
                setRole('super_admin')
                setProfileDegraded(false)
                return true
            }

            console.warn('[Auth] profile unavailable; keeping the established role')
            setRole(prev => roleAfterProfileFailure(prev))
            setProfileDegraded(true)
            return false
        } catch (error) {
            if (lastAuthedUserIdRef.current !== userId) return false
            console.error('[Auth] unexpected error resolving profile:', error)
            setRole(prev => roleAfterProfileFailure(prev))
            setProfileDegraded(true)
            return false
        }
    }

    // Initialize auth state
    useEffect(() => {
        let cancelled = false

        /**
         * Boot must always terminate. Every await here is timeout-bounded and
         * `setLoading(false)` lives in a `finally`, because the previous shape
         * (`getSession().then(...)` with no catch and the flag cleared inside
         * the callback) left the app on the loading screen forever whenever a
         * request hung or threw.
         */
        const bootstrap = async () => {
            try {
                const { data: { session } } = await withTimeout(
                    supabase.auth.getSession(),
                    BOOT_TIMEOUT_MS
                )
                if (cancelled) return

                setSession(session)
                setUser(session?.user ?? null)
                lastAuthedUserIdRef.current = session?.user?.id ?? null

                if (session?.user && session.access_token) {
                    await fetchProfile(session.user.id, session.access_token)
                }
            } catch (err) {
                console.error('[Auth] bootstrap failed; continuing signed-out:', err)
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        void bootstrap()

        // Capacitor restore can emit SIGNED_IN after a force-stop. That is not a
        // fresh login — flipping loading back to true here left the CHI portal
        // on the spinner until fetchProfile (or an untimed getSession) finished.
        const bootWatchdog = window.setTimeout(() => {
            if (!cancelled) setLoading(false)
        }, BOOT_TIMEOUT_MS + 2_000)

        // Listen for auth changes. The callback must stay synchronous: GoTrue
        // holds an internal lock until it returns, so awaiting getSession /
        // signOut / fetchProfile here deadlocks Sign Out.
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (_event, session) => {
                setSession(session)
                setUser(session?.user ?? null)

                if (!session?.user || !session.access_token) {
                    lastAuthedUserIdRef.current = null
                    isImpersonatingRef.current = false
                    setAuthProfile(null)
                    setImpersonatedProfile(null)
                    setIsImpersonating(false)
                    setOriginalRole(null)
                    setImpersonatedDisplayName(null)
                    api.setImpersonationUserId(null)
                    setRole(UNRESOLVED_ROLE)
                    setProfileDegraded(false)
                    setLoading(false)
                    return
                }

                const userId = session.user.id
                const accessToken = session.access_token
                window.setTimeout(() => {
                    void (async () => {
                        if (cancelled) return
                        try {
                            lastAuthedUserIdRef.current = userId
                            await fetchProfile(userId, accessToken)
                        } finally {
                            if (!cancelled) setLoading(false)
                        }
                    })()
                }, 0)
            }
        )

        return () => {
            cancelled = true
            window.clearTimeout(bootWatchdog)
            subscription.unsubscribe()
        }
    }, [])

    // Custom setRole that handles impersonation ("Login As" from User Management).
    // Passing displayName is the Login As signal — including admin → admin.
    // Optional profile snapshot swaps the effective UI profile + API data scope.
    const handleSetRole = (newRole: UserRole, options?: SetRoleOptions) => {
        const displayName = options?.displayName?.trim() || null
        const canImpersonate =
            role === 'admin' ||
            role === 'super_admin' ||
            !!authProfile?.roles?.includes('admin') ||
            !!authProfile?.roles?.includes('super_admin') ||
            isImpersonating

        if (canImpersonate && displayName) {
            if (!isImpersonating) {
                setOriginalRole(role)
                setIsImpersonating(true)
                isImpersonatingRef.current = true
            }
            setImpersonatedDisplayName(displayName)

            if (options?.profile?.id) {
                const snap = options.profile
                const assigned = Array.isArray(snap.assigned_stations)
                    ? snap.assigned_stations.map((s) => String(s).trim()).filter(Boolean)
                    : []
                const station = snap.station?.trim() || assigned[0] || ''
                const nextProfile: UserProfile = {
                    id: snap.id,
                    email: snap.email || '',
                    full_name: snap.full_name || displayName,
                    phone: snap.phone,
                    organization: snap.organization,
                    station: station || undefined,
                    assigned_stations: assigned.length > 0 ? assigned : (station ? [station] : []),
                    roles: Array.isArray(snap.roles) && snap.roles.length > 0
                        ? snap.roles
                        : [newRole],
                }
                setImpersonatedProfile(nextProfile)
                api.setImpersonationUserId(snap.id)
            } else {
                setImpersonatedProfile(null)
                api.setImpersonationUserId(null)
            }

            setRole(newRole)
            return
        }

        if (
            (newRole === 'admin' || newRole === 'super_admin') &&
            isImpersonating &&
            !displayName
        ) {
            if (originalRole) setRole(originalRole)
            isImpersonatingRef.current = false
            setIsImpersonating(false)
            setOriginalRole(null)
            setImpersonatedDisplayName(null)
            setImpersonatedProfile(null)
            api.setImpersonationUserId(null)
            return
        }

        if (isImpersonating && displayName) {
            setImpersonatedDisplayName(displayName)
        }
        setRole(newRole)
    }
    
    // Permission helpers
    const permissions = rolePermissions[role]

    const hasAccess = (page: keyof RolePermissions) => {
        return permissions[page] !== 'none'
    }

    const canEdit = (page: keyof RolePermissions) => {
        return permissions[page] === 'full'
    }

    // Auth actions
    const login = async (email: string, password: string): Promise<{ error?: string }> => {
        try {
            const { error } = await supabase.auth.signInWithPassword({ email, password })
            if (error) return { error: error.message }
            return {}
        } catch (e: any) {
            return { error: e.message || 'Login failed' }
        }
    }

    const loginWithOtp = async (tokenHash: string): Promise<{ error?: string }> => {
        try {
            const { error } = await supabase.auth.verifyOtp({
                token_hash: tokenHash,
                type: 'email',
            })
            if (error) return { error: error.message }
            return {}
        } catch (e: any) {
            return { error: e.message || 'OTP verification failed' }
        }
    }

    const signup = async (email: string, password: string, fullName: string): Promise<{ error?: string }> => {
        try {
            const { error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: { full_name: fullName }
                }
            })
            if (error) return { error: error.message }
            return {}
        } catch (e: any) {
            return { error: e.message || 'Signup failed' }
        }
    }

    const loginWithGoogle = async () => {
        await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: window.location.origin
            }
        })
    }

    /** Re-attempts profile resolution after a failed/degraded load. */
    const retryProfile = async (): Promise<boolean> => {
        const current = user
        if (!current) return false
        return fetchProfile(current.id, await currentAccessToken())
    }

    const logout = async () => {
        clearLocalSession()
        await persistSignOut()
    }
    
    // Exit impersonation mode (restore original admin role)
    const exitImpersonation = () => {
        if (originalRole) {
            setRole(originalRole)
        }
        isImpersonatingRef.current = false
        setIsImpersonating(false)
        setOriginalRole(null)
        setImpersonatedDisplayName(null)
        setImpersonatedProfile(null)
        api.setImpersonationUserId(null)
    }

    // Biometric auth actions
    const enableBiometric = async (email: string, password: string): Promise<boolean> => {
        if (!platform.isNative()) return false
        
        // First verify the credentials work
        const { error } = await login(email, password)
        if (error) return false
        
        // Store credentials securely
        const bio = await loadBiometricAuth()
        const stored = await bio.storeCredentials({ email, password })
        if (stored) {
            setBiometric(prev => ({ ...prev, isEnabled: true }))
        }
        return stored
    }

    const disableBiometric = async (): Promise<boolean> => {
        if (!platform.isNative()) return false
        
        const bio = await loadBiometricAuth()
        const deleted = await bio.deleteCredentials()
        if (deleted) {
            setBiometric(prev => ({ ...prev, isEnabled: false }))
        }
        return deleted
    }

    const loginWithBiometric = async (): Promise<{ error?: string }> => {
        if (!platform.isNative()) {
            return { error: 'Biometric login is only available on mobile' }
        }
        
        if (!biometric.isEnabled) {
            return { error: 'Biometric login is not enabled' }
        }
        
        try {
            const bio = await loadBiometricAuth()
            const credentials = await bio.biometricLogin()
            if (!credentials) {
                return { error: 'Biometric verification failed' }
            }
            
            // Login with stored credentials
            return await login(credentials.email, credentials.password)
        } catch (e: any) {
            return { error: e.message || 'Biometric login failed' }
        }
    }

    return (
        <AuthContext.Provider value={{
            user,
            profile,
            session,
            loading,
            role,
            setRole: handleSetRole,
            permissions,
            hasAccess,
            canEdit,
            login,
            loginWithOtp,
            signup,
            loginWithGoogle,
            logout,
            biometric,
            enableBiometric,
            disableBiometric,
            loginWithBiometric,
            isImpersonating,
            impersonatedDisplayName,
            impersonatedUserId: impersonatedProfile?.id ?? null,
            originalRole,
            exitImpersonation,
            profileDegraded,
            retryProfile,
            isSuperAdmin,
            subscription,
            refreshSubscription
        }}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
